import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  DollarSign,
  Trophy,
  RotateCcw,
  Sparkles,
  User,
  Check,
  PartyPopper,
  X,
} from "lucide-react";

const SPORTS_LIST = [
  "Aussie Rules", "Cricket", "Baseball", "Tennis", "Golf",
  "Bowls", "Hockey", "Table-Tennis", "Fishing", "Swimming",
  "Diving", "Rowing", "Sailing", "Netball", "Basketball",
  "Snooker", "Boxing", "Martial Arts", "Volleyball", "Motor Racing",
  "Wind Surfing", "Darts", "Shooting", "Vigoro", "Fencing",
  "Archery", "Horse Racing", "Skiing", "Rugby", "Cycling",
  "Aerobics", "Eightball", "Tri-Athlon", "Softball", "Squash",
  "Athletics", "Equestrian", "Soccer", "Surfing", "Badminton",
];

export default function PickASportsCard() {
  const navigate = useNavigate();
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entryFee, setEntryFee] = useState("5.00");
  const [editingFee, setEditingFee] = useState(false);
  const [claimModal, setClaimModal] = useState(null);
  const [playerName, setPlayerName] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [revealModal, setRevealModal] = useState(false);
  const [revealResult, setRevealResult] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [creating, setCreating] = useState(false);
  const nameInputRef = useRef(null);

  const loadRaffle = useCallback(async () => {
    try {
      const { data } = await api.get("/raffles/pick-a-sports-card/active");
      if (data) {
        setRaffle(data);
        setEntryFee(data.entry_fee?.toFixed(2) || "5.00");
      } else {
        setRaffle(null);
      }
    } catch (err) {
      if (err?.response?.status !== 404) {
        toast.error("Failed to load raffle");
      }
      setRaffle(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRaffle();
  }, [loadRaffle]);

  const createRaffle = async () => {
    setCreating(true);
    try {
      const fee = parseFloat(entryFee) || 0;
      const { data } = await api.post("/raffles/pick-a-sports-card", {
        entry_fee: fee,
      });
      setRaffle(data);
      toast.success("New raffle created!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create raffle");
    } finally {
      setCreating(false);
    }
  };

  const updateEntryFee = async () => {
    if (!raffle) return;
    try {
      const fee = parseFloat(entryFee) || 0;
      await api.patch(`/raffles/pick-a-sports-card/${raffle.id}/entry-fee`, {
        entry_fee: fee,
      });
      setRaffle((prev) => ({ ...prev, entry_fee: fee }));
      setEditingFee(false);
      toast.success("Entry fee updated");
    } catch (err) {
      toast.error("Failed to update entry fee");
    }
  };

  const claimSport = async () => {
    if (!raffle || !claimModal || !playerName.trim()) return;
    setClaiming(true);
    try {
      const { data } = await api.post(
        `/raffles/pick-a-sports-card/${raffle.id}/claim`,
        { sport: claimModal, player_name: playerName.trim() }
      );
      setRaffle((prev) => ({
        ...prev,
        claims: { ...prev.claims, [claimModal]: playerName.trim() },
        status: data.all_claimed ? "all_claimed" : prev.status,
      }));
      toast.success(`${playerName.trim()} claimed ${claimModal}!`);
      setClaimModal(null);
      setPlayerName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to claim sport");
    } finally {
      setClaiming(false);
    }
  };

  const revealWinner = async () => {
    if (!raffle) return;
    setRevealing(true);
    try {
      const { data } = await api.post(
        `/raffles/pick-a-sports-card/${raffle.id}/reveal`
      );
      setRevealResult(data);
      setRaffle((prev) => ({
        ...prev,
        revealed: true,
        winning_sport: data.winning_sport,
        winner_name: data.winner_name,
        status: "completed",
      }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reveal winner");
    } finally {
      setRevealing(false);
    }
  };

  const resetRaffle = async () => {
    if (!raffle) return;
    if (!window.confirm("Reset this raffle? All claims will be cleared and a new winning sport will be selected.")) return;
    setResetting(true);
    try {
      await api.post(`/raffles/pick-a-sports-card/${raffle.id}/reset`);
      setRevealResult(null);
      setRevealModal(false);
      await loadRaffle();
      toast.success("Raffle reset with a new winning sport!");
    } catch (err) {
      toast.error("Failed to reset raffle");
    } finally {
      setResetting(false);
    }
  };

  const claims = raffle?.claims || {};
  const totalClaimed = Object.keys(claims).length;
  const allClaimed = totalClaimed >= SPORTS_LIST.length;
  const isCompleted = raffle?.status === "completed" || raffle?.revealed;
  const fee = raffle?.entry_fee ?? (parseFloat(entryFee) || 0);

  // Loading skeleton
  if (loading) {
    return (
      <AppShell variant="admin" title="Pick A Sports Card">
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-16 w-full" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {Array.from({ length: 40 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  // No active raffle — create one
  if (!raffle) {
    return (
      <AppShell variant="admin" title="Pick A Sports Card">
        <div className="space-y-6" data-testid="no-raffle-view">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/a/raffles")}
            className="text-muted-foreground hover:text-[hsl(var(--fg))] -ml-2"
            data-testid="back-to-raffles"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Raffles
          </Button>

          <Card className="max-w-md mx-auto p-6 sm:p-8 text-center space-y-5 border border-[hsl(var(--border))]">
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[hsl(var(--primary)/0.08)] mx-auto">
              <Trophy className="h-7 w-7 text-[hsl(var(--primary))]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[hsl(var(--fg))]">Start a New Raffle</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Create a Pick A Sports Card raffle with 40 sports
              </p>
            </div>
            <div className="space-y-2 text-left">
              <Label htmlFor="create-fee" className="text-sm font-semibold">
                Entry Fee ($)
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="create-fee"
                  type="number"
                  step="0.50"
                  min="0"
                  value={entryFee}
                  onChange={(e) => setEntryFee(e.target.value)}
                  className="pl-9 h-11"
                  data-testid="create-entry-fee"
                />
              </div>
            </div>
            <Button
              onClick={createRaffle}
              disabled={creating}
              className="w-full h-11 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))] hover:opacity-90 font-semibold"
              data-testid="create-raffle-btn"
            >
              {creating ? "Creating..." : "Create Raffle"}
            </Button>
          </Card>
        </div>
      </AppShell>
    );
  }

  // Active raffle view
  return (
    <AppShell variant="admin" title="Pick A Sports Card">
      <div className="space-y-5" data-testid="sports-card-raffle">
        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/a/raffles")}
            className="text-muted-foreground hover:text-[hsl(var(--fg))] -ml-2 self-start"
            data-testid="back-to-raffles"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[hsl(var(--fg))] tracking-tight flex-1">
            Pick A Sports Card
          </h1>
        </div>

        {/* Stats bar */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Entry fee display/edit */}
          <div className="flex items-center gap-2 rounded-full px-4 py-2 bg-[hsl(var(--primary)/0.06)] border border-[hsl(var(--primary)/0.15)]">
            <DollarSign className="h-4 w-4 text-[hsl(var(--primary))]" />
            {editingFee ? (
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  step="0.50"
                  min="0"
                  value={entryFee}
                  onChange={(e) => setEntryFee(e.target.value)}
                  className="h-7 w-20 text-sm"
                  data-testid="edit-fee-input"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") updateEntryFee(); }}
                />
                <Button size="sm" onClick={updateEntryFee} className="h-7 px-2 bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))]" data-testid="save-fee-btn">
                  <Check className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditingFee(false); setEntryFee(raffle.entry_fee?.toFixed(2) || "0.00"); }} className="h-7 px-2">
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <button
                onClick={() => !isCompleted && setEditingFee(true)}
                className="text-sm font-semibold text-[hsl(var(--primary))] hover:underline disabled:opacity-50"
                disabled={isCompleted}
                data-testid="entry-fee-display"
              >
                Entry: ${fee.toFixed(2)}
              </button>
            )}
          </div>

          {/* Claimed counter */}
          <div className="flex items-center gap-2 rounded-full px-4 py-2 bg-[hsl(var(--secondary))] border border-[hsl(var(--border))]">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-semibold text-[hsl(var(--fg))]" data-testid="claimed-count">
              {totalClaimed} / {SPORTS_LIST.length} claimed
            </span>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 ml-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={resetRaffle}
              disabled={resetting}
              className="rounded-full text-xs h-9"
              data-testid="reset-raffle-btn"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              {resetting ? "Resetting..." : "Reset"}
            </Button>
          </div>
        </div>

        {/* Completed / Winner banner */}
        {isCompleted && raffle.revealed && (
          <Card className="border-2 border-[hsl(28,62%,44%)] bg-[hsl(28,62%,44%,0.04)] p-5 sm:p-6" data-testid="winner-banner">
            <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[hsl(28,62%,44%,0.12)]">
                <PartyPopper className="h-7 w-7 text-[hsl(28,62%,44%)]" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest font-bold text-[hsl(28,62%,44%)] mb-1">
                  Winner
                </div>
                <div className="text-xl sm:text-2xl font-extrabold text-[hsl(var(--fg))]" data-testid="winner-name">
                  {raffle.winner_name}
                </div>
                <div className="text-sm text-muted-foreground mt-0.5" data-testid="winning-sport">
                  Winning Sport: <span className="font-semibold text-[hsl(var(--fg))]">{raffle.winning_sport}</span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Reveal button — only when all 40 claimed and not yet revealed */}
        {allClaimed && !isCompleted && (
          <Button
            onClick={() => setRevealModal(true)}
            className="w-full sm:w-auto h-12 px-8 rounded-full bg-[hsl(28,62%,44%)] text-white hover:opacity-90 font-bold text-base animate-pulse"
            data-testid="reveal-winner-btn"
          >
            <Sparkles className="h-5 w-5 mr-2" />
            Reveal Winning Sport
          </Button>
        )}

        {/* Sports card image with overlaid buttons */}
        <div className="w-full max-w-[700px] mx-auto" data-testid="sports-card-grid">
          <div className="relative w-full" style={{ paddingBottom: "125%" }}>
            {/* Background image — the "Pick & Win" template */}
            <img
              src="/pick-win-sports-card.png"
              alt="Pick & Win Sports Card"
              className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
              draggable={false}
            />
            {/* 40 overlaid sport card buttons — pixel-measured on 1080×1350 source */}
            {SPORTS_LIST.map((sport, idx) => {
              const row = Math.floor(idx / 5);
              const col = idx % 5;
              const ROW_TOPS   = [24.52, 34.07, 43.63, 53.19, 62.74, 72.30, 81.85, 91.41];
              const COL_LEFTS  = [2.41, 21.85, 41.48, 61.11, 80.56];
              const CARD_W     = 16.94;
              const CARD_H     = 7.19;
              const topPct  = ROW_TOPS[row];
              const leftPct = COL_LEFTS[col];
              const isClaimed = sport in claims;
              const isWinner = isCompleted && raffle.revealed && sport === raffle.winning_sport;
              const claimedBy = claims[sport] || "";

              return (
                <button
                  key={sport}
                  onClick={() => {
                    if (isClaimed || isCompleted) return;
                    setClaimModal(sport);
                    setPlayerName("");
                  }}
                  disabled={isClaimed || isCompleted}
                  className={`absolute flex flex-col items-center justify-center text-center transition-all duration-200 overflow-hidden
                    focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:outline-none
                    ${isWinner
                      ? "bg-[hsl(28,62%,44%,0.92)] shadow-lg ring-2 ring-[hsl(28,62%,44%)]"
                      : isClaimed
                        ? "bg-[hsl(var(--primary)/0.85)] cursor-default"
                        : "bg-white/40 hover:bg-[hsl(210,60%,88%,0.85)] cursor-pointer active:scale-[0.96]"
                    }
                  `}
                  style={{
                    top:    `${topPct}%`,
                    left:   `${leftPct}%`,
                    width:  `${CARD_W}%`,
                    height: `${CARD_H}%`,
                  }}
                  data-testid={`sport-card-${sport.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span
                    className={`font-bold leading-tight px-0.5 ${
                      isWinner
                        ? "text-white"
                        : isClaimed
                          ? "text-white"
                          : "text-[hsl(210,45%,25%)]"
                    }`}
                    style={{ fontSize: "clamp(7px, 1.3vw, 13px)" }}
                  >
                    {sport}
                  </span>
                  {isClaimed && (
                    <span
                      className={`leading-tight truncate max-w-full px-0.5 ${
                        isWinner ? "text-white/90 font-semibold" : "text-white/80"
                      }`}
                      style={{ fontSize: "clamp(6px, 1vw, 10px)" }}
                    >
                      {claimedBy}
                    </span>
                  )}
                  {isWinner && (
                    <Trophy className="absolute top-0.5 right-0.5 text-white/90" style={{ width: "clamp(8px, 1.2vw, 14px)", height: "clamp(8px, 1.2vw, 14px)" }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Claim modal */}
        <Dialog open={!!claimModal} onOpenChange={(v) => { if (!v) { setClaimModal(null); setPlayerName(""); } }}>
          <DialogContent
            className="rounded-2xl max-w-sm"
            data-testid="claim-modal"
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              setTimeout(() => nameInputRef.current?.focus(), 100);
            }}
          >
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">
                Claim: {claimModal}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                Enter the player's name to claim this sport card.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="player-name" className="text-sm font-semibold">
                  Player Name
                </Label>
                <Input
                  ref={nameInputRef}
                  id="player-name"
                  placeholder="Enter player name..."
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && playerName.trim()) claimSport(); }}
                  className="h-11"
                  data-testid="player-name-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={claimSport}
                  disabled={!playerName.trim() || claiming}
                  className="flex-1 h-11 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))] hover:opacity-90 font-semibold"
                  data-testid="confirm-claim-btn"
                >
                  {claiming ? "Claiming..." : "Confirm"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setClaimModal(null); setPlayerName(""); }}
                  className="h-11 rounded-full px-5"
                  data-testid="cancel-claim-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reveal modal */}
        <Dialog open={revealModal} onOpenChange={setRevealModal}>
          <DialogContent className="rounded-2xl max-w-sm" data-testid="reveal-modal">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-center">
                {revealResult ? "We Have a Winner!" : "Reveal Winning Sport?"}
              </DialogTitle>
            </DialogHeader>
            {revealResult ? (
              <div className="text-center space-y-4 py-2">
                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-[hsl(28,62%,44%,0.12)] mx-auto">
                  <PartyPopper className="h-8 w-8 text-[hsl(28,62%,44%)]" />
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-[hsl(var(--fg))]" data-testid="reveal-winner-name">
                    {revealResult.winner_name}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1" data-testid="reveal-winning-sport">
                    Winning Sport: <span className="font-bold text-[hsl(28,62%,44%)]">{revealResult.winning_sport}</span>
                  </div>
                </div>
                <Button
                  onClick={() => setRevealModal(false)}
                  className="w-full h-11 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))]"
                  data-testid="close-reveal-btn"
                >
                  Done
                </Button>
              </div>
            ) : (
              <div className="text-center space-y-4 py-2">
                <p className="text-sm text-muted-foreground">
                  All 40 sports have been claimed. Are you ready to reveal the concealed winning sport?
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={revealWinner}
                    disabled={revealing}
                    className="flex-1 h-11 rounded-full bg-[hsl(28,62%,44%)] text-white hover:opacity-90 font-bold"
                    data-testid="confirm-reveal-btn"
                  >
                    {revealing ? "Revealing..." : "Reveal Now"}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setRevealModal(false)}
                    className="h-11 rounded-full px-5"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}
